import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useDebouncedCallback } from 'use-debounce';
import Loading from '../../shared/loading';

const WordCounter = styled.span`
  font-size: 14px;
  color: #627484;
  text-align: right;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 800;
  text-transform: uppercase;

  & > textarea {
    resize: vertical;
    height: ${props => props.height}px;

    ${props => (props.theme.isXXLarge && `
      height: ${props.height - 24}px;
    `)}

    ${props => (props.theme.isMobile && `
      height: ${props.height + 20}px;
    `)}
  }
`;

const Container = styled.div`
  display: flex;
  justify-content: space-between;
`;

const Error = styled.div`
  color: #B71E49;
  font-size: 14px;
  line-height: 1.5;
  text-align: left;
`;

const limiter = (limit, v) => ((limit && v) ? v.substring(0, limit) : v);

export default function TextArea({
  value, onChange, placeholder, label, limit, isLoading, height, error,
}) {
  const [buffer, setBuffer] = React.useState(value || '');
  const loadingRef = React.useRef();
  const { callback: passValueBack } = useDebouncedCallback(onChange, 300);

  React.useEffect(() => loadingRef.current?.toggle(isLoading));

  React.useEffect(() => {
    value && setBuffer(limiter(limit, value));
  }, [value, limit]);

  const handleChange = e => {
    const newBuffer = limiter(limit, e.target.value);
    setBuffer(newBuffer);
    passValueBack(newBuffer);
  };

  return (
    <>
      <Label height={height}>
        {label}
        <textarea
          placeholder={placeholder}
          value={buffer}
          onChange={handleChange}
        />
      </Label>
      <Container>
        <Error>{error}</Error>
        {!!limit && (
          <p className="sip-controls">
            <Loading variant="sip" ref={loadingRef} />
            <WordCounter>{`${buffer.length}/${limit}`}</WordCounter>
          </p>
        )}
      </Container>
    </>
  );
}

TextArea.propTypes = {
  value: PropTypes.string,
  placeholder: PropTypes.string,
  label: PropTypes.string,
  limit: PropTypes.number,
  height: PropTypes.number,
  isLoading: PropTypes.bool.isRequired,
  error: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

TextArea.defaultProps = {
  value: null,
  placeholder: null,
  label: null,
  limit: 140,
  height: 100,
  error: null,
};
